/**
 * 회의록 — 작성·보기 모달 (v2.118.0 · 2026-09-03 · 사용자 요청)
 *          + 참석자 동의 서명·변경 요청 (v2.229.0 · 2026-10-06 · 사용자 요청 「결재를 없애고 동의 서명으로」)
 *
 *   회의명 / 일시(날짜+시각, 회의시간 자동) / 장소 / 참석자(사내·외부) /
 *   안건 / 논의 내용 / 결정 사항 / 후속 조치(할 일·담당자·기한) / 비고
 *   + 참석자 동의 서명 · 변경 요청·답글
 *
 * ⭐ 작성자 본인은 **자동으로 1번 참석자**다 — 회색으로 고정해 두고 못 빼게 한다.
 * ⭐ 참석자로 넣은 사내 직원은 **그 회의록을 같이 보고, 서명 요청을 받는다** — 화면에 그렇게 적어 둔다.
 *   말해 주지 않으면 남의 이름을 무심코 넣는다.
 *
 * 흐름 (v2.229.0) — 결재가 없다:
 *   작성(임시저장) → [서명 요청] → 사내 참석자가 각자 읽고 [동의 서명] / [변경 요청]
 *   → 모두 서명하면 「서명 완료」. 상태 칸은 결재 엔진 것 — 작성중=임시저장 · 검토대기=서명 중 · 최종승인=서명 완료.
 * ⭐ 서명은 「그때 본 내용」에 대한 동의다 — 서명할 때 화면이 받아 둔 **내용 지문(hash)** 을 함께 보낸다.
 *   그 사이 작성자가 고쳤으면 서버가 거절하고, 화면은 새 내용을 다시 불러온다.
 * ⭐ 서명 단계에서 작성자가 내용(회의 자료 포함)을 고치면 받은 서명은 지워지고 다시 받는다 — 저장 전에 묻는다.
 *   서명 완료된 회의록은 바로 고치지 못하고 [✏️ 고치기]를 눌러야 입력칸이 열린다(실수로 고치지 않게).
 * ⭐ 결재로 끝난 옛 회의록(2026-09 결재 방식 · 운영 회의-2026-001)은 그때의 결재판을 그대로 보여 주고 고치지 않는다.
 */

import { todayKst } from '../lib/date-label';
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../lib/expense-api';
import * as meetingApi from '../lib/meeting-api';
import { EXPENSE_STATUS_BADGE } from './ExpenseDocModal';
import { ExpenseAttachBox } from './ExpenseAttachBox';
import { RichEditor } from './RichEditor';
import { EMPTY_DOC, MEETING_DOC_LIMITS, docForEdit, docForSave, type RichDoc } from '../lib/rich-doc';
import { signatureImageUrl } from '../lib/approval-api';
import { DocHistoryList } from './DocHistoryList';
import { openServerPdfPreview } from '../lib/pdf-preview';
import { useAuth } from '../lib/auth-context';
import { showToast } from '../lib/copy-toast';

interface Props {
  /** null = 새로 작성 */
  docId: number | null;
  onClose: () => void;
}

interface ExternalRow { name: string; org: string; title: string }
interface ActionRow { task: string; owner: string; dueDate: string }

const todayStr = (): string => todayKst();

/** 'YYYY-MM-DD HH:MM…' → '10-06 14:02' (서명·글 시각 — 연도는 뺀다) */
function shortStamp(ts: string | null | undefined): string {
  if (!ts) return '';
  const m = /^\d{4}-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(ts);
  return m ? `${m[1]}-${m[2]} ${m[3]}:${m[4]}` : ts.slice(5, 16);
}

function emptyForm() {
  return {
    title: '',
    meetingDate: todayStr(),
    startTime: '',
    endTime: '',
    place: '',
    agenda: '',
    // 논의 내용·결정 사항은 **서식 있는 문서**다 (v2.124.0) — 글·표·그림이 한 나무에 섞인다
    discussionDoc: EMPTY_DOC as RichDoc,
    decisionDoc: EMPTY_DOC as RichDoc,
    remark: '',
    internalIds: [] as string[],
    externals: [] as ExternalRow[],
    actionItems: [] as ActionRow[],
  };
}

export function MeetingMinutesModal({ docId, onClose }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [msg, setMsg] = useState<string | null>(null);
  /**
   * 아직 안 올라간 회의 자료 (v2.119.0) — **새로 쓰는 회의록에만** 쌓인다.
   * 새 문서는 문서번호가 없어 바로 올릴 수 없다. 저장·서명 요청으로 문서가 생긴 **뒤에** 올린다.
   */
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  /** 서명 완료된 회의록을 [✏️ 고치기]로 연 상태 (v2.229.0) — 저장하면 서명을 다시 받는다 */
  const [reopening, setReopening] = useState(false);
  /** 변경 요청·답글 입력칸 */
  const [commentText, setCommentText] = useState('');

  /** 문서 목록·결재함 배지를 함께 새로 — 서명하면 사이드바 [내 결재함] 숫자도 바로 줄어야 한다 */
  const invalidateAll = (): void => {
    qc.invalidateQueries({ queryKey: ['expense'] });
    qc.invalidateQueries({ queryKey: ['approvals', 'pendingCount'] });
  };

  const detail = useQuery({
    queryKey: ['expense', 'doc', docId],
    queryFn: () => api.getExpenseDoc(docId!),
    enabled: docId !== null,
  });

  const users = useQuery({
    queryKey: ['expense', 'users'],
    queryFn: api.listPortalUsers,
    staleTime: 300_000,
  });

  const doc = detail.data?.doc ?? null;
  const m = detail.data?.meeting ?? null;
  /** 동의 서명·변경 요청 (v2.229.0) — 옛 서버(배포 도중)는 안 준다 */
  const sign = detail.data?.meetingSign ?? null;
  const legacy = sign?.legacy ?? false;
  const canEdit = docId === null || (detail.data?.canEdit ?? false) || (reopening && (sign?.canReopen ?? false));
  /** 임시저장 단계인가 — [임시저장]·[서명 요청] 단추. 서명 단계면 [저장] 하나 */
  const isDraft = docId === null || doc?.status === '작성중' || doc?.status === '반려';
  /** 지금 살아 있는 서명 수 — 서명 단계에서 고칠 때 「지워집니다」 확인에 쓴다 */
  const signedNow = sign?.signed ?? 0;
  const isAuthorView = docId === null || doc?.authorId === user?.userId;
  /**
   * 회의 자료 첨부 (v2.119.0) — kind·이름은 **서버 EXPENSE_ATTACH_KIND 와 같은 표**에서 온다.
   * 여기에 문자열을 직접 적으면 서버와 갈리는 날 업로드가 조용히 거절된다.
   */
  const attachKind = api.expenseAttachKind('회의록');
  const attachLabel = api.EXPENSE_ATTACH_LABEL['회의록'];

  // 기존 문서를 열면 그 값으로 폼을 채운다.
  // ⚠deps 에 객체를 넣지 않는다 — 값으로만 넣어야 매 렌더마다 다시 돌지 않는다.
  useEffect(() => {
    if (!m) return;
    setForm({
      title: m.title,
      meetingDate: m.meetingDate,
      startTime: m.startTime,
      endTime: m.endTime,
      place: m.place,
      agenda: m.agenda,
      // 옛 회의록(글·표·그림 목록 시절)은 서버가 이미 문서 모양으로 옮겨 준다 — 여기서 또 만들지 않는다.
      // 저장된 그림은 번호만 오므로 볼 수 있는 주소를 붙인다(로그인 쿠키가 함께 간다).
      discussionDoc: docForEdit(m.discussionDoc, (id) => meetingApi.meetingImageUrl(docId!, id)),
      decisionDoc: docForEdit(m.decisionDoc, (id) => meetingApi.meetingImageUrl(docId!, id)),
      remark: m.remark,
      internalIds: m.attendees.filter((a) => a.seq > 1 && a.userId).map((a) => a.userId!),
      externals: m.attendees
        .filter((a) => a.isExternal)
        .map((a) => ({ name: a.name, org: a.org, title: a.title ?? '' })),
      actionItems: m.actionItems.map((a) => ({
        task: a.task, owner: a.owner, dueDate: a.dueDate ?? '',
      })),
    });
    // ⚠블록은 배열이라 그대로 deps 에 넣으면 매 렌더마다 다시 돈다 — **지문(문자열)**으로 넣는다.
  }, [m?.title, m?.meetingDate, m?.startTime, m?.endTime, m?.place, m?.agenda,
      m?.remark,
      JSON.stringify(m?.discussionDoc ?? null),
      JSON.stringify(m?.decisionDoc ?? null),
      m?.attendees.map((a) => `${a.userId ?? ''}|${a.name}|${a.org}`).join(','),
      m?.actionItems.map((a) => `${a.task}|${a.owner}|${a.dueDate ?? ''}`).join(',')]);

  const durText = meetingApi.localMeetingTimeText(form.startTime, form.endTime);

  /**
   * 1번 참석자(=작성자)를 뺀 직원 목록.
   * ⚠**「나」를 빼면 안 된다**(휴일근무일지 v2.102.1 과 같은 함정): 남이 쓴 회의록을
   *   참석자가 열면 자기 이름이 목록에서 사라져 자기가 들어가 있는 칸이 「— 없음 —」이 된다.
   */
  const authorUserId = m?.attendees?.[0]?.userId ?? user?.userId ?? '';
  const pickable = useMemo(
    () => (users.data ?? []).filter((u) => u.userId !== authorUserId),
    [users.data, authorUserId],
  );

  const save = useMutation({
    mutationFn: async (submit: boolean) => {
      /**
       * ⚠`discussion`·`decision` 글자는 **보내지 않는다**(빈 값). 서버가 블록에서 다시 만든다.
       *   여기서 채우면 블록과 어긋난 사본이 생길 자리가 열린다.
       */
      // 새 그림은 dataUrl 로, 저장된 그림은 번호만 — 화면 표시용 주소는 떼고 보낸다
      const { discussionDoc, decisionDoc, ...rest } = form;
      const input: meetingApi.MeetingSaveInput = {
        ...rest, discussion: '', decision: '', submit,
        discussionDoc: docForSave(discussionDoc),
        decisionDoc: docForSave(decisionDoc),
      };
      const r = docId === null
        ? await meetingApi.createMeetingDoc(input)
        : await meetingApi.updateMeetingDoc(docId, input);
      /**
       * 작성 화면에서 고른 회의 자료를 **문서가 생긴 뒤에** 올린다.
       * ⭐서명 요청(submit=true)과 함께 올려도 괜찮다 — 서명 중은 자료를 받아 주고, 받은 뒤 PDF 를 다시 만든다.
       *   (방금 서명 요청을 보낸 문서라 지워질 서명이 아직 없다.)
       */
      if (attachKind) for (const f of pendingFiles) await api.uploadReceipt(r.id, f, attachKind);
      return { ...r, submit };
    },
    onSuccess: (r) => {
      setPendingFiles([]);
      invalidateAll();
      const voided = 'voided' in r ? (r.voided ?? 0) : 0;
      if (r.submit) {
        showToast({
          text: r.status === '최종승인'
            ? '서명할 사내 참석자가 없어 바로 확정했습니다'
            : '서명 요청을 보냈습니다 — 사내 참석자에게 웍스 알림이 갑니다',
        });
      } else if (voided > 0) {
        showToast({ text: `저장했습니다 — 내용이 바뀌어 받은 서명 ${voided}건이 지워졌고, 다시 서명 요청을 보냈습니다` });
      }
      onClose();
    },
    onError: (e) => setMsg((e as Error).message),
  });

  /** 저장 단추 — 서명 단계에서 이미 받은 서명이 있으면 먼저 묻는다(내용이 바뀌면 지워진다) */
  function onSave(submit: boolean) {
    if (!isDraft && signedNow > 0) {
      const ok = window.confirm(
        `이미 ${signedNow}명이 서명했습니다.\n내용이 바뀌었으면 그 서명은 지워지고 다시 서명을 받습니다.\n\n저장할까요?`,
      );
      if (!ok) return;
    }
    save.mutate(submit);
  }

  /** 이미 있는 회의록에 자료를 더 붙인다 — 문서번호가 있으니 고르는 즉시 올라간다 */
  const attachMut = useMutation({
    mutationFn: async (files: File[]) => {
      if (attachKind) for (const f of files) await api.uploadReceipt(docId!, f, attachKind);
    },
    onSuccess: async () => {
      invalidateAll();
      await detail.refetch();
    },
    onError: (e) => setMsg((e as Error).message),
  });

  /** 서명 단계에서 자료를 바꾸기 전에 묻는다 — 받은 서명이 지워진다 */
  function confirmFileChange(): boolean {
    if (isDraft || signedNow === 0) return true;
    return window.confirm(
      `이미 ${signedNow}명이 서명했습니다.\n회의 자료를 바꾸면 그 서명은 지워지고 다시 서명을 받습니다.\n\n계속할까요?`,
    );
  }

  /* ── 동의 서명 (v2.229.0) ─────────────────────────────── */
  const signMut = useMutation({
    mutationFn: () => meetingApi.signMeeting(docId!, sign!.hash),
    onSuccess: async (r) => {
      showToast({ text: r.becameComplete ? '서명했습니다 — 모두 서명해 회의록이 확정됐습니다' : '서명했습니다' });
      invalidateAll();
      await detail.refetch();
    },
    onError: async (e) => {
      setMsg((e as Error).message);
      // 그 사이 내용이 바뀌었으면 새 내용을 불러와 다시 읽게 한다
      await detail.refetch();
    },
  });
  const unsignMut = useMutation({
    mutationFn: () => meetingApi.unsignMeeting(docId!),
    onSuccess: async () => {
      showToast({ text: '서명을 거뒀습니다' });
      invalidateAll();
      await detail.refetch();
    },
    onError: (e) => setMsg((e as Error).message),
  });

  /* ── 변경 요청 · 답글 (v2.229.0) ──────────────────────── */
  const commentMut = useMutation({
    mutationFn: (text: string) => meetingApi.addMeetingComment(docId!, text),
    onSuccess: async () => {
      setCommentText('');
      showToast({ text: isAuthorView ? '답글을 남겼습니다' : '변경 요청을 남겼습니다 — 작성자에게 웍스 알림이 갑니다' });
      invalidateAll();
      await detail.refetch();
    },
    onError: (e) => setMsg((e as Error).message),
  });
  const resolveMut = useMutation({
    mutationFn: (v: { cid: number; resolved: boolean }) => meetingApi.resolveMeetingComment(docId!, v.cid, v.resolved),
    onSuccess: async () => {
      invalidateAll();
      await detail.refetch();
    },
    onError: (e) => setMsg((e as Error).message),
  });
  const delCommentMut = useMutation({
    mutationFn: (cid: number) => meetingApi.deleteMeetingComment(docId!, cid),
    onSuccess: async () => {
      invalidateAll();
      await detail.refetch();
    },
    onError: (e) => setMsg((e as Error).message),
  });

  const del = useMutation({
    mutationFn: () => api.deleteExpenseDoc(docId!),
    onSuccess: () => {
      invalidateAll();
      onClose();
    },
    onError: (e) => window.alert((e as Error).message),
  });

  function setInternal(idx: number, value: string) {
    setForm((f) => {
      const next = [...f.internalIds];
      if (!value) next.splice(idx, 1);
      else next[idx] = value;
      return { ...f, internalIds: [...new Set(next.filter(Boolean))] };
    });
  }

  const myName = user?.name ?? user?.userId ?? '';
  const authorRow = docId !== null && m
    ? m.attendees.find((a) => a.seq === 1)
    : { name: myName, title: null as string | null, department: null as string | null };

  const internalCount = form.internalIds.length + 1;
  const totalAttendees = internalCount + form.externals.filter((e) => e.name.trim()).length;
  const inputCls = 'border border-slate-300 rounded px-2 py-1 text-sm disabled:bg-slate-50';
  const statusLabel = doc ? meetingApi.meetingStatusLabel(doc.status, legacy) : '';
  const openRequests = (sign?.comments ?? []).filter((c) => c.isRequest && !c.resolvedAt).length;

  return (
    <div
      className="fixed inset-0 bg-black/40 z-[70] flex items-start justify-center overflow-y-auto p-4"
    >
      <div className="bg-white rounded shadow-xl w-full max-w-3xl my-6">
        {/* 머리 */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h2 className="ui-modal-title">회의록</h2>
            {doc && <span className="text-xs text-slate-500">{doc.docNo}</span>}
            {doc && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${EXPENSE_STATUS_BADGE[doc.status] ?? ''}`}>
                {statusLabel}
                {sign && !legacy && doc.status === '검토대기' && sign.required > 0 && ` ${sign.signed}/${sign.required}`}
              </span>
            )}
            {reopening && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">고치는 중 — 저장하면 서명을 다시 받습니다</span>
            )}
          </div>
          <button type="button" onClick={onClose} className="ui-close">✕</button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {doc?.rejectReason && (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2">
              반려 사유: {doc.rejectReason}
            </div>
          )}
          {legacy && (
            <div className="text-xs bg-slate-50 border border-slate-200 text-slate-600 rounded px-3 py-2">
              이 회의록은 <b>결재 방식(2026-09)</b>으로 확정된 문서입니다 — 그때의 결재판 그대로 보관합니다.
              고칠 내용이 있으면 새 회의록으로 남겨 주세요.
            </div>
          )}

          {/* 회의 개요 */}
          <section className="space-y-2">
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">회의명</div>
              <input type="text" value={form.title} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="예) 2026년 9월 영업·기술지원 합동 주간회의"
                className={`w-full ${inputCls}`} />
            </div>
            {/* ⚠시간 칸 폭 — 한국어 크롬은 「오전 09:00 🕐」로 그려서 116px 이 있어야 안 잘린다
                (2026-08-31 실측). 여유 8px 두고 124px.
                ⭐폰(640px 미만)은 한 줄에 다 안 들어가 끝 시각이 창 밖으로 잘렸다 — 참석자가 웍스 알림을 받고
                폰으로 열어 서명하게 되면서(v2.229.0) 좁은 화면만 줄이 접히게 했다. 넓은 화면은 예전 격자 그대로. */}
            <div className="flex flex-wrap gap-2 items-center sm:grid sm:grid-cols-[46px_150px_124px_16px_124px_1fr]">
              <span className="text-slate-500 text-sm w-[46px]">일시</span>
              <input type="date" value={form.meetingDate} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, meetingDate: e.target.value }))}
                className={`w-[150px] ${inputCls}`} />
              <input type="time" value={form.startTime} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                className={`w-[124px] ${inputCls}`} />
              <span className="text-slate-500 text-xs text-center w-4">~</span>
              <input type="time" value={form.endTime} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                className={`w-[124px] ${inputCls}`} />
              <span className="text-slate-500 text-xs">
                {durText ? <b className="text-slate-700">( {durText} )</b> : '시각은 안 적어도 됩니다'}
              </span>
            </div>
            <div className="grid grid-cols-[46px_1fr] gap-2 items-center">
              <span className="text-slate-500 text-sm">장소</span>
              <input type="text" value={form.place} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, place: e.target.value }))}
                placeholder="예) 본사 회의실 / 화상(Teams)"
                className={inputCls} />
            </div>
          </section>

          {/* 참석자 */}
          <section>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">
              참석자 <span className="font-normal text-slate-400">
                {totalAttendees}명 (최대 {meetingApi.MAX_ATTENDEES}명)
              </span>
            </div>
            <div className="space-y-1.5 text-sm">
              {/* 1번 = 작성자 본인. 자동으로 들어가고 뺄 수 없다 */}
              <div className="flex items-center gap-2">
                <span className="w-14 text-slate-500 text-xs shrink-0">사내 1</span>
                <div className="flex-1 border border-slate-200 bg-slate-50 rounded px-2 py-1 text-slate-700">
                  {authorRow?.name ?? myName}
                  <span className="text-slate-500 text-xs ml-2">
                    {[authorRow?.title, authorRow?.department].filter(Boolean).join(' · ') || '본인'}
                  </span>
                </div>
              </div>
              {/* 사내 참석자 — 고른 만큼 + 빈 칸 하나 (다 채우면 칸이 하나 더 생긴다) */}
              {Array.from({ length: Math.min(form.internalIds.length + (canEdit ? 1 : 0), meetingApi.MAX_ATTENDEES - 1) }, (_, i) => {
                const sel = form.internalIds[i] ?? '';
                return (
                  <div key={i} className="flex items-center gap-2">
                    <span className="w-14 text-slate-500 text-xs shrink-0">사내 {i + 2}</span>
                    <select
                      value={sel}
                      disabled={!canEdit}
                      onChange={(e) => setInternal(i, e.target.value)}
                      className="ui-input ui-input-sm flex-1"
                    >
                      <option value="">— 없음 —</option>
                      {pickable
                        .filter((u) => u.userId === sel || !form.internalIds.includes(u.userId))
                        .map((u) => (
                          <option key={u.userId} value={u.userId}>
                            {u.name}{u.title ? ` ${u.title}` : ''}{u.department ? ` · ${u.department}` : ''}
                          </option>
                        ))}
                    </select>
                  </div>
                );
              })}
              {/* 외부 참석자 — 계정이 없으므로 이름·소속·직급을 직접 적는다 */}
              {form.externals.map((ex, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-14 text-amber-600 text-xs shrink-0">외부 {i + 1}</span>
                  <input type="text" value={ex.name} disabled={!canEdit}
                    onChange={(e) => setForm((f) => {
                      const next = [...f.externals];
                      next[i] = { ...next[i]!, name: e.target.value };
                      return { ...f, externals: next };
                    })}
                    placeholder="이름" className={`w-[110px] ${inputCls}`} />
                  <input type="text" value={ex.title} disabled={!canEdit}
                    onChange={(e) => setForm((f) => {
                      const next = [...f.externals];
                      next[i] = { ...next[i]!, title: e.target.value };
                      return { ...f, externals: next };
                    })}
                    placeholder="직급" className={`w-[90px] ${inputCls}`} />
                  <input type="text" value={ex.org} disabled={!canEdit}
                    onChange={(e) => setForm((f) => {
                      const next = [...f.externals];
                      next[i] = { ...next[i]!, org: e.target.value };
                      return { ...f, externals: next };
                    })}
                    placeholder="소속 (회사·기관)" className={`flex-1 min-w-0 ${inputCls}`} />
                  {canEdit && (
                    <button type="button" title="이 줄 지우기"
                      onClick={() => setForm((f) => ({ ...f, externals: f.externals.filter((_, k) => k !== i) }))}
                      className="text-slate-400 hover:text-rose-600 px-1">✕</button>
                  )}
                </div>
              ))}
              {canEdit && form.externals.length < meetingApi.MAX_ATTENDEES && (
                <button type="button"
                  onClick={() => setForm((f) => ({ ...f, externals: [...f.externals, { name: '', org: '', title: '' }] }))}
                  className="text-xs text-blue-600 hover:underline">+ 외부 참석자 추가</button>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              직급·부서는 직원 정보에서 자동으로 들어갑니다. ⭐<b>사내 참석자로 넣은 분들은 이 회의록을 볼 수 있고,
              [서명 요청]을 누르면 동의 서명 요청을 받습니다.</b> 외부 참석자는 계정이 없어 서명 대상이 아닙니다.
            </p>
          </section>

          {/* 안건 · 논의 내용 · 결정 사항 */}
          <section className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">
                안건 <span className="font-normal text-slate-400">(한 줄에 하나씩)</span>
              </div>
              <textarea rows={3} value={form.agenda} disabled={!canEdit}
                onChange={(e) => setForm((f) => ({ ...f, agenda: e.target.value }))}
                placeholder={'예) 9월 수주 목표 점검\n유지보수 계약 갱신 일정\n재고 부족 품목 대응'}
                className="ui-input ui-textarea w-full" />
            </div>
            {/* 논의 내용 — 글과 표가 섞인다 (v2.120.0). 엑셀 표를 붙여넣으면 그 자리에 표가 들어간다 */}
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">
                논의 내용 <span className="font-normal text-slate-400">
                  (엑셀 표나 그림을 복사해 붙여넣으면 그 자리에 들어갑니다)
                </span>
              </div>
              <RichEditor
                label="논의 내용"
                value={form.discussionDoc}
                onChange={(next) => setForm((f) => ({ ...f, discussionDoc: next }))}
                readOnly={!canEdit}
                limits={MEETING_DOC_LIMITS}
                fullWidthTables
                minHeight={200}
                placeholder="안건별로 오간 이야기를 적습니다."
              />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-700 mb-1">
                결정 사항 <span className="font-normal text-slate-400">(한 줄에 하나씩 · 표·그림도 넣을 수 있습니다)</span>
              </div>
              <RichEditor
                label="결정 사항"
                value={form.decisionDoc}
                onChange={(next) => setForm((f) => ({ ...f, decisionDoc: next }))}
                readOnly={!canEdit}
                limits={MEETING_DOC_LIMITS}
                fullWidthTables
                minHeight={110}
                placeholder="예) 9월 목표 12억 유지 · 갱신 견적은 9/10 까지 발송"
              />
            </div>
          </section>

          {/* 후속 조치 */}
          <section>
            <div className="text-xs font-semibold text-slate-700 mb-1.5">
              후속 조치 <span className="font-normal text-slate-400">(할 일 · 담당자 · 기한)</span>
            </div>
            <div className="space-y-1.5">
              {form.actionItems.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-6 text-slate-500 text-xs text-right shrink-0">{i + 1}</span>
                  <input type="text" value={it.task} disabled={!canEdit}
                    onChange={(e) => setForm((f) => {
                      const next = [...f.actionItems];
                      next[i] = { ...next[i]!, task: e.target.value };
                      return { ...f, actionItems: next };
                    })}
                    placeholder="할 일" className={`flex-1 min-w-0 ${inputCls}`} />
                  <input type="text" value={it.owner} disabled={!canEdit}
                    onChange={(e) => setForm((f) => {
                      const next = [...f.actionItems];
                      next[i] = { ...next[i]!, owner: e.target.value };
                      return { ...f, actionItems: next };
                    })}
                    placeholder="담당자" className={`w-[110px] ${inputCls}`} />
                  <input type="date" value={it.dueDate} disabled={!canEdit}
                    onChange={(e) => setForm((f) => {
                      const next = [...f.actionItems];
                      next[i] = { ...next[i]!, dueDate: e.target.value };
                      return { ...f, actionItems: next };
                    })}
                    className={`w-[150px] ${inputCls}`} />
                  {canEdit && (
                    <button type="button" title="이 줄 지우기"
                      onClick={() => setForm((f) => ({ ...f, actionItems: f.actionItems.filter((_, k) => k !== i) }))}
                      className="text-slate-400 hover:text-rose-600 px-1">✕</button>
                  )}
                </div>
              ))}
              {canEdit && form.actionItems.length < meetingApi.MAX_ACTION_ITEMS && (
                <button type="button"
                  onClick={() => setForm((f) => ({ ...f, actionItems: [...f.actionItems, { task: '', owner: '', dueDate: '' }] }))}
                  className="text-xs text-blue-600 hover:underline">+ 후속 조치 추가</button>
              )}
              {form.actionItems.length === 0 && (
                <p className="text-xs text-slate-500">후속 조치가 없으면 비워 두어도 됩니다.</p>
              )}
            </div>
          </section>

          {/* 비고 */}
          <section>
            <div className="text-xs font-semibold text-slate-700 mb-1">비고</div>
            <textarea rows={2} value={form.remark} disabled={!canEdit}
              onChange={(e) => setForm((f) => ({ ...f, remark: e.target.value }))}
              className="ui-input ui-textarea w-full" />
          </section>

          {/* 회의 자료 첨부 (v2.119.0) — 확정본 회의록 뒤에 이어 붙는다.
              새 회의록은 문서번호가 없어 담아 뒀다가 저장할 때 올린다(queue). */}
          {attachKind !== null && (
            <div>
              <ExpenseAttachBox
                label={attachLabel}
                uploaded={detail.data?.receipts ?? []}
                pending={pendingFiles}
                queue={docId === null}
                canAdd={canEdit && !attachMut.isPending}
                canDelete={docId !== null && canEdit}
                pendingWhen="저장할 때 올라감"
                hint="회의록 뒤에 함께 묶입니다 · PDF · 이미지 · 엑셀"
                pendingNote="방금 고른 파일은 [임시저장]·[서명 요청] 을 누른 뒤에 올라갑니다."
                onPick={(files) => {
                  if (docId === null) setPendingFiles((p) => [...p, ...files]);
                  else if (confirmFileChange()) attachMut.mutate(files);
                }}
                onRemovePending={(i) => setPendingFiles((p) => p.filter((_, j) => j !== i))}
                onDelete={(r) => {
                  if (!confirmFileChange()) return;
                  api.deleteReceipt(r.id)
                    .then(() => { invalidateAll(); return detail.refetch(); })
                    .catch((e: Error) => setMsg(e.message));
                }}
              />
              {canEdit && !isDraft && signedNow > 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  ⚠ 자료를 바꾸면 받은 서명 {signedNow}건이 지워지고 다시 서명을 받습니다.
                </p>
              )}
            </div>
          )}

          {/* 참석자 동의 서명 (v2.229.0) — 결재판 자리. 옛 결재 문서는 아래 결재판을 그대로 */}
          {sign && !legacy && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                참석자 동의 서명{' '}
                <span className="font-normal text-slate-400">
                  {doc?.status === '작성중' || doc?.status === '반려'
                    ? '— [서명 요청]을 누르면 사내 참석자에게 서명 요청이 갑니다'
                    : sign.required === 0
                      ? '— 서명할 사내 참석자가 없어 작성자 확정으로 끝났습니다'
                      : `${sign.signed}/${sign.required}명 서명${sign.complete ? ' · 서명 완료' : ''}`}
                </span>
              </div>
              <ul className="border border-slate-200 rounded divide-y divide-slate-100 text-sm">
                {/* 작성자 — [서명 요청]을 누른 것이 작성자의 서명이다 */}
                <li className="flex items-center gap-2 px-3 py-1.5">
                  <span className="w-16 shrink-0 text-xs text-slate-500">작성자</span>
                  <span className="flex-1 min-w-0 truncate">{sign.author.name}</span>
                  {sign.author.at && doc?.status !== '작성중' && doc?.status !== '반려' ? (
                    <span className="shrink-0 flex items-center gap-1.5 text-xs text-slate-500">
                      {sign.author.hasSignature && (
                        <img src={signatureImageUrl(sign.author.userId)} alt="" className="max-h-[22px] max-w-[60px] object-contain"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      )}
                      작성 {shortStamp(sign.author.at)}
                    </span>
                  ) : (
                    <span className="shrink-0 text-xs text-slate-500">서명 요청 전</span>
                  )}
                </li>
                {sign.signers.map((s) => (
                  <li key={s.userId} className="flex items-center gap-2 px-3 py-1.5">
                    <span className="w-16 shrink-0 text-xs text-slate-500">참석자</span>
                    <span className="flex-1 min-w-0 truncate">
                      {s.name}
                      <span className="text-slate-500 text-xs ml-1.5">{[s.title, s.department].filter(Boolean).join(' · ')}</span>
                      {s.userId === user?.userId && <span className="ml-1.5 text-[11px] px-1 rounded bg-blue-50 text-blue-700">나</span>}
                    </span>
                    {!s.active ? (
                      <span className="shrink-0 text-xs text-slate-500">계정 없음 — 서명 생략</span>
                    ) : s.signedAt ? (
                      <span className="shrink-0 flex items-center gap-1.5 text-xs text-emerald-700">
                        {s.hasSignature && (
                          <img src={signatureImageUrl(s.userId)} alt="" className="max-h-[22px] max-w-[60px] object-contain"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        )}
                        ✔ 동의 {shortStamp(s.signedAt)}
                      </span>
                    ) : (
                      <span className="shrink-0 text-xs text-amber-700">
                        {doc?.status === '작성중' || doc?.status === '반려' ? '서명 요청 전' : s.stale ? '내용이 바뀌어 다시 서명 필요' : '서명 전'}
                      </span>
                    )}
                  </li>
                ))}
                {sign.signers.length === 0 && (
                  <li className="px-3 py-1.5 text-xs text-slate-500">
                    작성자 말고는 사내 참석자가 없습니다 — 서명 요청을 누르면 바로 확정됩니다.
                  </li>
                )}
              </ul>
              {/* 내 차례 — 읽고 동의하면 서명. 고칠 곳이 있으면 아래 변경 요청으로 */}
              {sign.canSign && (
                <div className="mt-2 flex items-center justify-between gap-3 flex-wrap rounded border border-emerald-200 bg-emerald-50 px-3 py-2">
                  <span className="text-xs text-emerald-900">
                    위 회의 내용을 읽고 동의하면 서명해 주세요. 고칠 곳이 있으면 아래 <b>변경 요청</b>을 남겨 주세요.
                  </span>
                  <button type="button"
                    disabled={signMut.isPending}
                    onClick={() => {
                      if (window.confirm('이 회의록 내용에 동의하고 서명합니다.\n\n서명할까요?')) signMut.mutate();
                    }}
                    className="btn btn-success-solid">
                    {signMut.isPending ? '서명 중…' : '✍️ 동의 서명'}
                  </button>
                </div>
              )}
              {sign.canUnsign && (
                <div className="mt-1.5 text-right">
                  <button type="button" disabled={unsignMut.isPending}
                    onClick={() => {
                      if (window.confirm('내 서명을 거둘까요?\n(다시 서명할 수 있습니다)')) unsignMut.mutate();
                    }}
                    className="text-xs text-slate-500 hover:text-rose-600 underline disabled:opacity-40">
                    내 서명 거두기
                  </button>
                </div>
              )}
            </section>
          )}

          {/* 변경 요청 · 답글 (v2.229.0) — 참석자가 남기면 변경 요청, 작성자가 남기면 답글. 글자만(서식 없음) */}
          {sign && !legacy && (sign.comments.length > 0 || sign.canComment) && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">
                변경 요청 · 답글{' '}
                <span className="font-normal text-slate-400">
                  {sign.comments.length}건{openRequests > 0 ? ` · 반영 안 된 요청 ${openRequests}건` : ''}
                </span>
              </div>
              {sign.comments.length > 0 && (
                <ul className="space-y-1.5 mb-2">
                  {sign.comments.map((c) => (
                    <li key={c.id}
                      className={`rounded border px-3 py-2 text-sm ${
                        c.isRequest
                          ? (c.resolvedAt ? 'border-slate-200 bg-slate-50' : 'border-amber-200 bg-amber-50/60')
                          : 'border-blue-100 bg-blue-50/50'}`}>
                      <div className="flex items-center gap-1.5 flex-wrap text-xs text-slate-500">
                        <span className={`px-1.5 py-0.5 rounded font-semibold ${
                          c.isRequest ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                          {c.isRequest ? '변경 요청' : '작성자 답글'}
                        </span>
                        <span className="font-semibold text-slate-700">{c.writerName}</span>
                        <span>{shortStamp(c.createdAt)}</span>
                        {c.isRequest && c.resolvedAt && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                            ✔ 반영함{c.resolvedByName ? ` · ${c.resolvedByName}` : ''}
                          </span>
                        )}
                        <span className="flex-1" />
                        {c.canResolve && (
                          <button type="button" disabled={resolveMut.isPending}
                            onClick={() => resolveMut.mutate({ cid: c.id, resolved: !c.resolvedAt })}
                            className="text-blue-600 hover:underline disabled:opacity-40">
                            {c.resolvedAt ? '반영 표시 풀기' : '반영함으로 표시'}
                          </button>
                        )}
                        {c.canDelete && (
                          <button type="button" disabled={delCommentMut.isPending}
                            onClick={() => { if (window.confirm('이 글을 지울까요?')) delCommentMut.mutate(c.id); }}
                            className="text-slate-400 hover:text-rose-600 hover:underline disabled:opacity-40">
                            지우기
                          </button>
                        )}
                      </div>
                      {/* 글자 그대로 — 서식·태그를 해석하지 않는다(React 가 글자로 그린다) */}
                      <p className="mt-1 whitespace-pre-wrap break-words text-slate-800">{c.body}</p>
                    </li>
                  ))}
                </ul>
              )}
              {sign.canComment && (
                <div className="space-y-1.5">
                  <textarea rows={2} value={commentText} maxLength={2000}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder={isAuthorView
                      ? '답글 — 예) 결정 사항 2번 고쳤습니다. 다시 확인 부탁드립니다.'
                      : '고칠 곳을 적어 주세요 — 예) 결정 사항 2번: 납기 9/10 → 9/15'}
                    className="ui-input ui-textarea w-full" />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-slate-500">
                      {isAuthorView
                        ? '고친 뒤 저장하면 받은 서명이 지워지고 참석자에게 다시 서명 요청이 갑니다.'
                        : '남기면 작성자에게 웍스 알림이 갑니다.'}
                    </span>
                    <button type="button"
                      disabled={commentMut.isPending || !commentText.trim()}
                      onClick={() => commentMut.mutate(commentText.trim())}
                      className="btn btn-warning">
                      {commentMut.isPending ? '남기는 중…' : isAuthorView ? '답글 남기기' : '변경 요청 남기기'}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* 결재판 — **결재로 끝난 옛 회의록만** (2026-09 결재 방식 · 3칸). 새 회의록은 서버가 칸을 주지 않는다 */}
          {detail.data && detail.data.cells.length > 0 && (
            <section>
              <div className="text-xs font-semibold text-slate-700 mb-1.5">결재</div>
              <div className="flex gap-2">
                {detail.data.cells.map((c) => (
                  <div key={c.role} className="border border-slate-300 rounded text-center w-[86px]">
                    <div className="text-[11px] bg-slate-100 py-0.5 text-slate-600">{c.role}</div>
                    <div className="h-9 flex items-center justify-center text-xs">
                      {c.skipped ? (
                        <span className="text-slate-300">생략</span>
                      ) : c.stamped ? (
                        c.userId && c.hasSignature ? (
                          <img src={signatureImageUrl(c.userId)} alt={c.name}
                            className="max-h-[30px] max-w-[70px] object-contain"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        ) : (
                          <span className="font-semibold text-slate-700">{c.name}</span>
                        )
                      ) : (
                        <span className="text-slate-300">대기</span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 pb-0.5">
                      {c.skipped ? '' : (c.stampedAt ?? '').slice(2, 10)}
                      {!c.skipped && c.delegated && (
                        <span className="ml-0.5 text-[11px] text-amber-600 font-semibold">(대결)</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 처리 이력 — 서명 요청·서명·고침(서명 무효)이 남는다 */}
          <DocHistoryList history={detail.data?.history} />

          {msg && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-3 py-2">{msg}</div>}
        </div>

        {/* 단추 */}
        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3 flex-wrap">
          <div className="flex gap-2">
            {detail.data?.attachmentId && (
              <button type="button"
                onClick={() => openServerPdfPreview({
                  pdfUrl: api.attachmentDownloadUrl(detail.data!.attachmentId!),
                  filename: `${doc?.docNo ?? '회의록'}_회의록.pdf`,
                })}
                className="btn btn-secondary">
                📄 문서 보기
              </button>
            )}
            {detail.data?.canDelete && (
              <button type="button"
                onClick={() => {
                  if (window.confirm(`${doc?.docNo ?? '이 회의록'} 을 삭제할까요?`)) del.mutate();
                }}
                disabled={del.isPending}
                className="btn btn-danger">
                {del.isPending ? '삭제 중…' : '🗑 삭제'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {/* 서명 완료된 회의록 — 바로 고치지 못하게 한 번 묻고 입력칸을 연다 */}
            {sign?.canReopen && !reopening && (
              <button type="button"
                onClick={() => {
                  const ok = window.confirm(
                    '모든 참석자가 서명한 회의록입니다.\n고쳐서 저장하면 받은 서명이 모두 지워지고 다시 서명을 받아야 합니다.\n\n고칠까요?',
                  );
                  if (ok) setReopening(true);
                }}
                className="btn btn-secondary">
                ✏️ 고치기
              </button>
            )}
            {canEdit && isDraft && (
              <>
                <button type="button" onClick={() => onSave(false)} disabled={save.isPending}
                  className="btn btn-secondary">
                  {save.isPending ? '저장 중…' : '임시저장'}
                </button>
                <button type="button" onClick={() => onSave(true)} disabled={save.isPending}
                  className="btn btn-primary">
                  ✍️ 서명 요청
                </button>
              </>
            )}
            {canEdit && !isDraft && (
              <button type="button" onClick={() => onSave(false)} disabled={save.isPending}
                className="btn btn-primary">
                {save.isPending ? '저장 중…' : '저장'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
